/**
 * ArtNexus - Supabase Client Configuration
 * 
 * @author Prompt007dev
 * @created 2024
 * @description Supabase client setup and database type definitions
 */

// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { PostgrestError } from '@supabase/supabase-js';
import { toast } from 'sonner';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Since we can't modify types.ts, we'll define basic types here that match our schema
export interface Profile {
  id: string;
  username: string;
  avatar: string;
  bio?: string;
  location?: string;
  created_at?: string;
}

export interface Artist {
  id: string;
  name: string;
  bio?: string;
  location?: string;
  photo?: string;
  verified: boolean;
  created_at?: string;
}

export interface Artwork {
  id: string;
  title: string;
  description?: string;
  medium?: string;
  year?: string;
  category?: string;
  image: string;
  artist_id: string;
  aspectratio?: string;
  created_at?: string;
}

export interface Collection {
  id: string;
  name: string;
  description?: string | null;
  cover_image?: string | null;
  user_id: string;
  created_at?: string;
  collection_items?: CollectionItem[];
}

export interface CollectionItem {
  id: string;
  collection_id: string;
  artwork_id: string;
  created_at?: string;
}

export interface MarketplaceItem {
  id: string;
  artwork_id: string;
  price: string;
  type: string;
  status: string;
  created_at?: string;
}

export interface Event {
  id: string;
  title: string;
  description?: string;
  date: string;
  time?: string;
  location?: string;
  image?: string;
  featured?: boolean;
  created_at?: string;
}

export interface ForumTopic {
  id: string;
  title: string;
  category: string;
  author_id: string;
  reply_count: number;
  created_at?: string;
}

export interface ForumPost {
  id: string;
  topic_id: string;
  author_id: string;
  content: string;
  created_at?: string;
  profiles?: Profile;
}

export interface ArtClass {
  id: string;
  title: string;
  description?: string;
  image?: string;
  video_url?: string;
  level?: string;
  duration?: string;
  category?: string;
  price?: number;
  instructor: string;
  instructor_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Performance {
  id: string;
  title: string;
  description?: string;
  date: string;
  time?: string;
  location?: string;
  artist: string;
  image?: string;
  created_at?: string;
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";
export const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);

/**
 * Ensures that a specific storage bucket exists
 * @param bucketName The name of the bucket to check/create
 * @returns Boolean indicating if the bucket exists or was created successfully
 */
export const ensureStorageBucket = async (bucketName: string): Promise<boolean> => {
  try {
    // First check if the bucket exists
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();
    
    if (listError) {
      console.error("Error checking buckets:", listError);
      return false;
    }
    
    // If bucket already exists, return true
    if (buckets.some(bucket => bucket.name === bucketName)) {
      console.log(`Bucket ${bucketName} already exists`);
      return true;
    }
    
    // If bucket doesn't exist, create it
    const { data, error: createError } = await supabase.storage.createBucket(bucketName, {
      public: true,
      fileSizeLimit: 10485760, // 10MB in bytes
    });
    
    if (createError) {
      console.error(`Error creating bucket ${bucketName}:`, createError);
      return false;
    }
    
    console.log(`Successfully created bucket ${bucketName}`);
    return true;
  } catch (error) {
    console.error(`Unexpected error ensuring bucket ${bucketName}:`, error);
    return false;
  }
};

/**
 * Ensures an artist record exists for the current user
 * @param userId The ID of the user to check/create artist record for
 * @returns Boolean indicating if the artist exists or was created successfully
 */
export const ensureArtistExists = async (userId: string): Promise<boolean> => {
  try {
    // First check if artist already exists
    const { data: existingArtist, error: checkError } = await supabase
      .from('artists')
      .select('id')
      .eq('id', userId)
      .single();
    
    if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "no rows returned" error
      console.error("Error checking for existing artist:", checkError);
      return false;
    }
    
    // If artist already exists, return true
    if (existingArtist) {
      console.log(`Artist record for user ${userId} already exists`);
      return true;
    }
    
    // Look up user profile information
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (profileError) {
      console.error("Error fetching user profile:", profileError);
      // Continue anyway - we'll create artist with minimal info
    }
    
    // Create new artist record
    const { error: insertError } = await supabase
      .from('artists')
      .insert({
        id: userId,
        name: profile?.username || 'Artist',
        bio: profile?.bio || '',
        location: profile?.location || '',
        photo: profile?.avatar || '',
        verified: false
      });
    
    if (insertError) {
      console.error("Error creating artist record:", insertError);
      return false;
    }
    
    console.log(`Successfully created artist record for user ${userId}`);
    return true;
  } catch (error) {
    console.error(`Unexpected error ensuring artist exists for ${userId}:`, error);
    return false;
  }
};

/**
 * Helper function to handle Supabase errors consistently
 * @param error The error object from Supabase
 * @param fallbackMessage A fallback message to display if the error doesn't have a message
 * @returns A user-friendly error message
 */
export const handleSupabaseError = (error: any, fallbackMessage = 'An unexpected error occurred'): string => {
  if (!error) return fallbackMessage;
  
  // Check if it's a Supabase error with message
  if (error.message) return error.message;
  
  // Check if it's a Supabase error with details
  if (error.details) return error.details;
  
  // Check if it has an error property (might be nested)
  if (error.error?.message) return error.error.message;
  
  // Return fallback message if all else fails
  return fallbackMessage;
};

/**
 * Service for handling collections-related operations
 */
export const collectionsService = {
  /**
   * Add an artwork to a collection
   */
  addToCollection: async (userId: string, collectionId: string, artworkId: string) => {
    const { error } = await supabase
      .from('collection_items')
      .insert({ collection_id: collectionId, artwork_id: artworkId });
    
    return { error };
  },
  
  /**
   * Remove an artwork from a collection
   */
  removeFromCollection: async (collectionId: string, artworkId: string) => {
    const { error } = await supabase
      .from('collection_items')
      .delete()
      .match({ collection_id: collectionId, artwork_id: artworkId });
    
    return { error };
  },

  /**
   * Get all collections for a user
   */
  getUserCollections: async () => {
    const { data, error } = await supabase
      .from('collections')
      .select(`
        *,
        collection_items(*)
      `)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error("Error fetching collections:", error);
      toast.error(handleSupabaseError(error, "Failed to load collections"));
    }
    
    return { data, error };
  },
  
  /**
   * Create a new collection
   */
  createCollection: async (name: string, description: string = "") => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      toast.error("You must be logged in to create a collection");
      return { error: new Error("User not authenticated") };
    }
    
    const { data, error } = await supabase
      .from('collections')
      .insert({
        name,
        description,
        user_id: userData.user.id,
      })
      .select()
      .single();
    
    return { data, error };
  },
  
  /**
   * Update an existing collection
   */
  updateCollection: async (id: string, updates: { name?: string; description?: string; cover_image?: string }) => {
    const { data, error } = await supabase
      .from('collections')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    return { data, error };
  },
  
  /**
   * Delete a collection
   */
  deleteCollection: async (id: string) => {
    const { error } = await supabase
      .from('collections')
      .delete()
      .eq('id', id);
    
    return { error };
  }
};
